import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Dot from 'components/Dot';
import FeatureNotSupported from 'components/FeatureNotSupported';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import { IA_KEY } from 'helpers/ia-logic';
import { selectActiveVenue, selectIaLogic, selectUserId } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import HowSmartRateWorks from './HowSmartRateWorks';
import WeeklyEarnings from './WeeklyEarnings';
import SmartRateFaqs from './SmartRateFaqs';
import { isSmartRateFeatureEnabled } from './SmartRate.helpers';
import { useSmartRateFaqsQuery } from './SmartRate.hooks';
import messages from './SmartRate.messages';

import './SmartRate.scss';

const { element } = bem('smart-rate');

const SmartRate = () => {
  const isMobile = useSelector(selectIsMobile);
  const { venueId, countryCodeShort } = useSelector(selectActiveVenue);
  const userId = useSelector(selectUserId);
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.SMART_RATE, venueId));
  const [isFeatureFlagEnabled, setIsFeatureFlagEnabled] = useState(null);
  const isSmartRateFeatureFlagReady = isFeatureFlagEnabled === true;
  const { data: faqs = [], isLoading } = useSmartRateFaqsQuery({
    enabled: featureIsSupported && isSmartRateFeatureFlagReady,
  });

  useEffect(() => {
    if (!featureIsSupported) {
      return undefined;
    }

    let cancelled = false;
    setIsFeatureFlagEnabled(null);

    const fetchFeatureFlag = async () => {
      try {
        const enabled = await isSmartRateFeatureEnabled(userId, venueId, countryCodeShort);
        if (!cancelled) {
          setIsFeatureFlagEnabled(enabled);
        }
      } catch (error) {
        reportError('Error checking feature flag for SmartRate', {
          error,
        });
        if (!cancelled) {
          setIsFeatureFlagEnabled(false);
        }
      }
    };

    fetchFeatureFlag();

    return () => {
      cancelled = true;
    };
  }, [countryCodeShort, featureIsSupported, userId, venueId]);

  if (!featureIsSupported) {
    return <FeatureNotSupported />;
  }

  if (isFeatureFlagEnabled === null) {
    return <LoadingDots />;
  }

  if (!isFeatureFlagEnabled) {
    return <FeatureNotSupported />;
  }

  return (
    <>
      <Message message={messages.title} as={Title} />
      {isMobile ? (
        <>
          <div className={element('intro')}>
            <HowSmartRateWorks />
          </div>
          <div className={element('content-sheet')}>
            <WeeklyEarnings layout="embedded" />
            <SmartRateFaqs faqs={faqs} isLoading={isLoading} layout="embedded" />
          </div>
        </>
      ) : (
        <>
          <div className="smart-rate__status">
            <Dot color={Dot.COLORS.GREEN} />
            <Message message={messages.statusOn} />
          </div>
          <HowSmartRateWorks />
          <WeeklyEarnings layout="card" />
          <SmartRateFaqs faqs={faqs} isLoading={isLoading} layout="card" />
        </>
      )}
    </>
  );
};

export default SmartRate;
